import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowUpRightIcon } from "lucide-react";
import { projects } from "../data/projects";
import { caseStudies } from "../data/caseStudies";
import { DISPLAY_HEADING, EYEBROW, H2, LEAD } from "../utils/typography";
import { TreatedImage } from "../components/TreatedImage";
import { ImagePlaceholder } from "../components/caseStudy/ImagePlaceholder";
import { ChallengeSolution } from "../components/caseStudy/ChallengeSolution";
import { ResultsSection } from "../components/caseStudy/ResultsSection";
import { ProcessSection } from "../components/caseStudy/ProcessSection";
import { FeaturesSection } from "../components/caseStudy/FeaturesSection";
import { KeyDecisions } from "../components/caseStudy/KeyDecisions";
import { BeforeAfter } from "../components/caseStudy/BeforeAfter";
import { ReflectionSection } from "../components/caseStudy/ReflectionSection";
import { GoDeeper } from "../components/caseStudy/GoDeeper";
import { RelatedThoughts } from "../components/caseStudy/RelatedThoughts";
import { Reveal } from "../components/Reveal";
import { usePageTitle } from "../hooks/usePageTitle";
export function CaseStudy() {
  const {
    id
  } = useParams();
  const caseStudyProjects = projects.filter(p => p.kind === "case-study");
  const index = caseStudyProjects.findIndex(p => p.id === id);
  const project = caseStudyProjects[index];
  const content = project ? caseStudies[project.id] : undefined;
  usePageTitle(project && content ? project.title : "Project not found");
  if (!project || !content) {
    return <main id="main-content" tabIndex={-1} className="flex min-h-screen w-full flex-col items-start justify-center bg-cream px-6 md:px-12 lg:px-20" data-id="element-426" data-mp="3272ywl7tnbwb">
        <p className="text-sm font-medium uppercase tracking-wide text-rust" data-id="element-427" data-mp="2x9ue0cvagtrd">Not found</p>
        <h1 className={`mt-4 ${DISPLAY_HEADING} text-espresso`} data-id="element-428" data-mp="hioqkfry7byf">
          This project isn’t here.
        </h1>
        <Link to="/#projects" className="mt-10 inline-flex items-center gap-2 rounded-full bg-espresso px-6 py-3 text-sm font-medium uppercase text-cream transition-colors duration-150 hover:bg-rust" data-id="element-429" data-mp="cjv9yehd74u1">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-430" data-mp="3f9niqc04ld4a" />
          All projects
        </Link>
      </main>;
  }

  // Follow homepage order, looping from the last case study back to the first
  const next = caseStudyProjects[(index + 1) % caseStudyProjects.length];
  const nextLabel = `Next project / ${caseStudies[next.id]?.number ?? ""}`;
  return <main id="main-content" tabIndex={-1} className="w-full bg-cream pb-24 pt-36 md:pb-32 md:pt-48 lg:pb-40" data-id="element-431" data-mp="2rkueztd9i0z4">
      <article data-id="element-432" data-mp="2whkw8k0sz2m4">
        {/* Title */}
        <Reveal className="px-6 md:px-12 lg:px-20" data-id="element-433" data-mp="31axlmwdb3dfx">
        <header data-id="element-434" data-mp="31bp6pd9a1sry">
          <Link to="/#projects" className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-espresso/70 transition-colors duration-150 hover:text-rust" data-id="element-435" data-mp="31c8pew6r4l71">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-436" data-mp="21abzm0u71vn8" />
            All projects
          </Link>
          <p className="mt-10 text-sm font-medium uppercase tracking-wide text-rust" data-id="element-437" data-mp="1wbhdaox0poyd">
            Case study / {content.number} · {project.client}
          </p>
          <h1 className={`mt-4 max-w-[16ch] ${DISPLAY_HEADING} text-espresso`} data-id="element-438" data-mp="1ral17o2iiwhv">
            {project.title}
          </h1>
        </header>
        </Reveal>

        {/* Hero image */}
        <Reveal className="mt-12 px-6 md:mt-16 md:px-12 lg:px-20" delay={0.06} data-id="element-439" data-mp="1ichqznwz9m5d">
          {project.image ? <TreatedImage src={project.image} alt={`${project.title} — ${project.client}`} className="aspect-[16/9] w-full rounded-3xl bg-paper" motionMask={project.motionMask} motionStyle={project.motionStyle} eager data-id="element-440" data-mp="1djomn15f8ftt" /> : <ImagePlaceholder caption="Hero image" className="aspect-[16/9] w-full" data-id="element-441" data-mp="um59pmfued24" />}
        </Reveal>

        <div className="px-6 md:px-12 lg:px-20" data-id="element-442" data-mp="2uon0ve5xo722">
          {/* Lede + facts */}
          <Reveal className="mt-16 grid grid-cols-1 gap-12 md:mt-24 lg:grid-cols-12" data-id="element-443" data-mp="2uqr37ro6oy08">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-6 self-start lg:col-span-4 lg:grid-cols-1" data-id="element-444" data-mp="2ys2qvrnhz95s">
              {content.facts.map(fact => <div key={fact.label} data-id="element-445" data-mp="2pwv950b1jvac">
                  <dt className={`${EYEBROW} text-espresso/70`} data-id="element-446" data-mp="2pvtrgczisajn">
                    {fact.label}
                  </dt>
                  <dd className="mt-1 text-espresso" data-id="element-447" data-mp="2fzh1nq73igu6">{fact.value}</dd>
                </div>)}
            </dl>
            <p className={`${LEAD} text-espresso lg:col-span-8`} data-id="element-448" data-mp="2208ykm7taokp">
              {content.lede}
            </p>
          </Reveal>

          <div className="mt-24 space-y-24 md:mt-32 md:space-y-32 lg:mt-40 lg:space-y-40" data-id="element-449" data-mp="26zlm796ibzoc">
            <Reveal data-id="element-450" data-mp="9dyvjahi58p">
              <ChallengeSolution challenge={content.challenge} solution={content.solution} data-id="element-451" data-mp="9499wtcq03j" />
            </Reveal>
            <Reveal data-id="element-452" data-mp="55ctvpqbili3">
              <ResultsSection results={content.results} data-id="element-453" data-mp="fbdw3lfykf8o" />
            </Reveal>
            {content.features && <Reveal data-id="element-454" data-mp="act3a731cxu9">
                <FeaturesSection features={content.features} data-id="element-455" data-mp="adkoqp2x5f6t" />
              </Reveal>}
            <Reveal data-id="element-456" data-mp="o4ppjui0wdiq">
              <ProcessSection process={content.process} data-id="element-457" data-mp="o4z7cuswzedc" />
            </Reveal>
            <Reveal data-id="element-458" data-mp="jc63081cy81s">
              <KeyDecisions decisions={content.decisions} data-id="element-459" data-mp="jboc7qxgsm19" />
            </Reveal>
            {content.beforeAfter && <Reveal data-id="element-460" data-mp="y3otx0vr9mzx">
                <BeforeAfter panels={content.beforeAfter} data-id="element-461" data-mp="t3ahz7r19swm" />
              </Reveal>}
            <Reveal data-id="element-462" data-mp="172a5ud6q23rq">
              <ReflectionSection learned={content.learned} data-id="element-463" data-mp="172tmzrojso4p" />
            </Reveal>
            <Reveal data-id="element-464" data-mp="130f79gg6e4hn">
              <RelatedThoughts slugs={content.relatedThoughts} data-id="element-465" data-mp="13203dai51vhm" />
            </Reveal>
            <Reveal data-id="element-466" data-mp="38t54rdw86j2o">
              <GoDeeper deeper={content.deeper} data-id="element-467" data-mp="38uolf5lsmw4z" />
            </Reveal>
          </div>
        </div>
      </article>

      {/* Next project */}
      {next && <Reveal data-id="element-468" data-mp="17rxjvpe5jfqd">
        <nav aria-label="Next project" className="mx-6 mt-24 md:mx-12 md:mt-32 lg:mx-20 lg:mt-40" data-id="element-469" data-mp="17sh113vza03c">
          <Link to={next.href} className="group flex items-end justify-between gap-6" data-id="element-470" data-mp="1cpz1e5vxj6zq">
            <div data-id="element-471" data-mp="1hpbz5loc9yh3">
              <p className="text-sm font-medium uppercase tracking-wide text-espresso/70" data-id="element-472" data-mp="1hottxzjp89x1">
                {nextLabel}
              </p>
              <p className={`mt-3 ${H2} text-espresso transition-colors duration-150 group-hover:text-rust`} data-id="element-473" data-mp="1mq9cnkyem4c9">
                {next.title}
              </p>
            </div>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-espresso text-cream transition-colors duration-150 group-hover:bg-rust" data-id="element-474" data-mp="1voblunsak3am">
              <ArrowUpRightIcon className="h-5 w-5" data-id="element-475" data-mp="25fz7u962ws4p" />
            </span>
          </Link>
        </nav>
        </Reveal>}
    </main>;
}